import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";

/**
 * Workshop: Projects section for Settings.
 * Two views: project list (rows) and project detail (drill-down).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Agent {
  id: string;
  name: string;
  active: boolean;
}

interface Workflow {
  id: string;
  name: string;
  status: "active" | "paused";
}

interface Skill {
  id: string;
  name: string;
  enabled: boolean;
}

interface Collaborator {
  id: string;
  name: string;
  role: "owner" | "editor" | "viewer";
}

interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  lastOpened: string;
  memberCount: number;
  agents: Agent[];
  workflows: Workflow[];
  skills: Skill[];
  collaborators: Collaborator[];
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const MOCK_PROJECTS: Project[] = [
  {
    id: "p1",
    name: "Marketing Website Redesign",
    description: "Complete overhaul of the marketing site with new branding.",
    createdAt: "Feb 15, 2026",
    lastOpened: "2 hours ago",
    memberCount: 4,
    agents: [
      { id: "a1", name: "Coder", active: true },
      { id: "a2", name: "Researcher", active: true },
      { id: "a3", name: "Writer", active: false },
    ],
    workflows: [
      { id: "w1", name: "Daily SEO Audit", status: "active" },
      { id: "w2", name: "Content Generation", status: "paused" },
    ],
    skills: [
      { id: "sk1", name: "Web Search", enabled: true },
      { id: "sk2", name: "Code Execution", enabled: true },
      { id: "sk3", name: "Image Generation", enabled: false },
    ],
    collaborators: [
      { id: "u1", name: "Alex Chen", role: "owner" },
      { id: "u2", name: "Maria Santos", role: "editor" },
      { id: "u3", name: "Jordan Lee", role: "editor" },
      { id: "u4", name: "Sam Rivera", role: "viewer" },
    ],
  },
  {
    id: "p2",
    name: "Mobile App V2",
    description: "Native mobile app with offline-first architecture.",
    createdAt: "Jan 20, 2026",
    lastOpened: "3 days ago",
    memberCount: 6,
    agents: [
      { id: "a1", name: "Coder", active: true },
      { id: "a4", name: "Tester", active: true },
    ],
    workflows: [{ id: "w3", name: "CI/CD Pipeline Monitor", status: "active" }],
    skills: [
      { id: "sk1", name: "Web Search", enabled: true },
      { id: "sk2", name: "Code Execution", enabled: true },
    ],
    collaborators: [
      { id: "u1", name: "Alex Chen", role: "owner" },
      { id: "u5", name: "Taylor Kim", role: "editor" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function FieldGroup({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <span className="text-[10px] font-medium uppercase tracking-wider text-secondary">
        {label}
      </span>
      {children}
    </div>
  );
}

function ToggleSwitch({
  enabled,
  onChange,
  testId,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      className={cn(
        "relative h-5 w-9 shrink-0 rounded-full transition-colors",
        enabled
          ? "bg-accent"
          : "border border-glass-stroke-light bg-glass-fill-heavy",
      )}
      onClick={() => onChange(!enabled)}
      data-testid={testId}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
          enabled ? "left-[18px]" : "left-0.5",
        )}
      />
    </button>
  );
}

const ROLE_BADGE_VARIANT = {
  owner: "default",
  editor: "selected",
  viewer: "outline",
} as const;

// ---------------------------------------------------------------------------
// Project List View
// ---------------------------------------------------------------------------

function ProjectListView({
  projects,
  onSelect,
  testId,
}: {
  projects: Project[];
  onSelect: (id: string) => void;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {projects.map((project) => (
        <button
          key={project.id}
          type="button"
          className="flex items-center gap-3 rounded-lg border border-glass-stroke-subtle p-3 text-left hover:bg-btn-ghost-fill"
          onClick={() => onSelect(project.id)}
          data-testid={`project-row-${project.id}`}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[11px] font-medium text-primary">
              {project.name}
            </span>
            <span className="text-[10px] text-secondary">
              Created {project.createdAt} &middot; {project.memberCount} members
              &middot; Last opened {project.lastOpened}
            </span>
          </div>
          <Icons.ChevronRight className="h-4 w-4 shrink-0 text-muted" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project Detail View
// ---------------------------------------------------------------------------

function ProjectDetailView({
  project,
  onBack,
  testId,
}: {
  project: Project;
  onBack: () => void;
  testId: string;
}) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description);
  const [agents, setAgents] = useState<Agent[]>(project.agents);
  const [skills, setSkills] = useState<Skill[]>(project.skills);

  function handleAgentToggle(agentId: string) {
    setAgents((prev) =>
      prev.map((a) => (a.id === agentId ? { ...a, active: !a.active } : a)),
    );
  }

  function handleSkillToggle(skillId: string) {
    setSkills((prev) =>
      prev.map((s) => (s.id === skillId ? { ...s, enabled: !s.enabled } : s)),
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid={testId}>
      {/* Back button */}
      <button
        type="button"
        className="flex items-center gap-1.5 text-[11px] text-secondary transition-colors hover:text-primary"
        onClick={onBack}
        data-testid="project-detail-back"
      >
        <Icons.ArrowLeft className="h-3.5 w-3.5" />
        Back to projects
      </button>

      {/* Name & Description */}
      <FieldGroup label="Project name" testId="project-detail-name-field">
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="project-detail-name-input"
        />
      </FieldGroup>

      <FieldGroup label="Description" testId="project-detail-description-field">
        <TextInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="project-detail-description-input"
        />
      </FieldGroup>

      {/* Active agents */}
      <FieldGroup label="Active agents" testId="project-detail-agents">
        <div className="flex flex-col gap-1.5">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="flex items-center justify-between rounded-lg border border-glass-stroke-subtle p-3 hover:bg-btn-ghost-fill"
              data-testid={`project-agent-${agent.id}`}
            >
              <span className="text-[11px] text-primary">{agent.name}</span>
              <ToggleSwitch
                enabled={agent.active}
                onChange={() => handleAgentToggle(agent.id)}
                testId={`project-agent-toggle-${agent.id}`}
              />
            </div>
          ))}
        </div>
      </FieldGroup>

      {/* Workflows */}
      <FieldGroup label="Workflows" testId="project-detail-workflows">
        <div className="flex flex-col gap-1.5">
          {project.workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="flex items-center justify-between rounded-lg border border-glass-stroke-subtle p-3 hover:bg-btn-ghost-fill"
              data-testid={`project-workflow-${workflow.id}`}
            >
              <span className="text-[11px] text-primary">{workflow.name}</span>
              <Badge
                variant={workflow.status === "active" ? "default" : "warning"}
                data-testid={`project-workflow-badge-${workflow.id}`}
              >
                {workflow.status}
              </Badge>
            </div>
          ))}
        </div>
      </FieldGroup>

      {/* Skills */}
      <FieldGroup label="Skills" testId="project-detail-skills">
        <div className="flex flex-col gap-1.5">
          {skills.map((skill) => (
            <div
              key={skill.id}
              className="flex items-center justify-between rounded-lg border border-glass-stroke-subtle p-3 hover:bg-btn-ghost-fill"
              data-testid={`project-skill-${skill.id}`}
            >
              <span className="text-[11px] text-primary">{skill.name}</span>
              <ToggleSwitch
                enabled={skill.enabled}
                onChange={() => handleSkillToggle(skill.id)}
                testId={`project-skill-toggle-${skill.id}`}
              />
            </div>
          ))}
        </div>
      </FieldGroup>

      {/* Collaborators */}
      <FieldGroup label="Collaborators" testId="project-detail-collaborators">
        <div className="flex flex-col gap-1.5">
          {project.collaborators.map((collab) => (
            <div
              key={collab.id}
              className="flex items-center justify-between rounded-lg border border-glass-stroke-subtle p-3 hover:bg-btn-ghost-fill"
              data-testid={`project-collaborator-${collab.id}`}
            >
              <span className="text-[11px] text-primary">{collab.name}</span>
              <Badge
                variant={ROLE_BADGE_VARIANT[collab.role]}
                data-testid={`project-collaborator-badge-${collab.id}`}
              >
                {collab.role}
              </Badge>
            </div>
          ))}
        </div>
        <Button variant="secondary" size="sm" data-testid="project-invite-btn">
          <Icons.Plus className="h-3.5 w-3.5" />
          Invite
        </Button>
      </FieldGroup>

      {/* Actions */}
      <div
        className="flex items-center gap-2 border-t border-glass-stroke-subtle pt-4"
        data-testid="project-detail-actions"
      >
        <Button variant="secondary" size="sm" data-testid="project-archive-btn">
          Archive
        </Button>
        <Button
          variant="destructive"
          size="sm"
          data-testid="project-delete-btn"
        >
          <Icons.Trash2 className="h-3.5 w-3.5" />
          Delete
        </Button>
        <div className="flex-1" />
        <Button size="sm" data-testid="project-save-btn">
          Save
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectsSection (main export)
// ---------------------------------------------------------------------------

interface ProjectsSectionProps {
  className?: string;
  "data-testid"?: string;
}

export function ProjectsSection({
  className,
  "data-testid": testId = "settings-projects",
}: ProjectsSectionProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedProject = selectedId
    ? (MOCK_PROJECTS.find((p) => p.id === selectedId) ?? null)
    : null;

  if (selectedProject) {
    return (
      <div className={className}>
        <ProjectDetailView
          project={selectedProject}
          onBack={() => setSelectedId(null)}
          testId={`${testId}-detail`}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <ProjectListView
        projects={MOCK_PROJECTS}
        onSelect={setSelectedId}
        testId={testId}
      />
    </div>
  );
}

export type { ProjectsSectionProps };
